import {Component, HostListener, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {RecaptchaComponent, ReCaptchaV3Service} from 'ng-recaptcha-2';
import {TranslateService} from '@ngx-translate/core';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {StateService} from '@uirouter/angular';
import {ButtonStyle} from '../../components/ui/text-button/text-button.component';
import {STATE_NAMES} from '../../common/state-names';
import {EMAIL_PATTERN} from '../../common/patterns';
import {RoutingService} from '../../original-services/routing.service';
import {GAService} from '../../original-services/ga.service';
import {LoggerService} from '../../original-services/logger.service';
import {SettingsService} from '../../original-services/settings.service';
import {AuthenticationService} from '../../original-services/authentication.service';
import {SignUpFormData, toSignUpApiPayload} from 'app/models/auth/sign-up';
import {Utils} from 'app/common/utils';
import {agreeToTermsValidator, serverRejectedValueValidator} from 'app/common/validators/auth.validators';
import { IntercomService } from 'app/services/intercom/intercom.service';

interface SignUpRouteParams {
    subscription?: string;
    user?: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

interface SignUpServerErrorBody {
    code?: string;
    billing_plan?: string;
}

export enum SignUpStep {
    Email = 'email',
    Name = 'name',
    Confirmation = 'confirmation'
}

@Component({
    selector: 'page-sign-up',
    templateUrl: './sign-up.component.html',
    standalone: false
})
export class SignUpPageComponent implements OnInit, OnDestroy {
    transPrefix = 'SignUp.';
    formPrefix = 'Form.';
    buttonStyle = ButtonStyle;
    signUpStep = SignUpStep;
    signInState = STATE_NAMES.login;
    errors: Observable<string>[] = [];
    submitPressed = false;
    submitInFlight = false;
    validationFailed = false;
    currentStep: SignUpStep = SignUpStep.Email;
    billingPlan: string | undefined;
    ucc: string | null = null;
    gclid: string | null = null;

    firstNameCtrl: FormControl<string>;
    lastNameCtrl: FormControl<string>;
    emailCtrl: FormControl<string>;
    agreeToTermsCtrl: FormControl<boolean>;
    form: FormGroup<{
        firstName: FormControl<string>;
        lastName: FormControl<string>;
        email: FormControl<string>;
        agreeToTerms: FormControl<boolean>;
    }>;

    private destroy$ = new Subject<void>();
    private serverRejectedEmail: string | null = null;
    private readonly stepEnterHandlers: Partial<Record<SignUpStep, () => void>> = {
        [SignUpStep.Email]: () => this.submitEmailStep(),
        [SignUpStep.Name]: () => this.signUp()
    };

    constructor(
        private authService: AuthenticationService,
        private routingService: RoutingService,
        private settingsService: SettingsService,
        private logger: LoggerService,
        public translate: TranslateService,
        private gaService: GAService,
        private stateService: StateService,
        public fb: FormBuilder,
        public intercom: IntercomService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private renderer: Renderer2
    ) {
        const params = this.routingService.params as SignUpRouteParams;
        this.billingPlan = params.subscription;

        this.firstNameCtrl = new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(30)]
        });
        this.lastNameCtrl = new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(30)]
        });
        this.emailCtrl = new FormControl('', {
            nonNullable: true,
            validators: [
                Validators.required,
                Validators.pattern(EMAIL_PATTERN),
                serverRejectedValueValidator(() => this.serverRejectedEmail)
            ]
        });
        this.agreeToTermsCtrl = new FormControl(false, {
            nonNullable: true,
            validators: [agreeToTermsValidator]
        });

        if (params.user) {
            this.firstNameCtrl.setValue(params.user.firstName);
            this.lastNameCtrl.setValue(params.user.lastName);
            this.emailCtrl.setValue(params.user.email);
            this.currentStep = SignUpStep.Name;
        }

        this.emailCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (this.serverRejectedEmail !== null && this.emailCtrl.value !== this.serverRejectedEmail) {
                this.clearErrors();
                this.validationFailed = false;
                this.serverRejectedEmail = null;
            }
        });

        this.form = this.fb.group({
            firstName: this.firstNameCtrl,
            lastName: this.lastNameCtrl,
            email: this.emailCtrl,
            agreeToTerms: this.agreeToTermsCtrl
        });

        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.validationFailed = false;
        });
    }

    ngOnInit() {
        if (!this.settingsService.hideRecaptcha) {
            this.renderer.addClass(document.body, 'show-recaptcha');
        }
        this.intercom.shutdown();
        this.gaService.trackPage(`/${this.stateService.current.name}${this.stateService.current.url}.html`);
        // Workaround: ng-recaptcha fails to unsubscribe in its own ngOnDestroy.
        RecaptchaComponent.prototype.ngOnDestroy = function() {
            if (this.subscription) {
                this.subscription.unsubscribe();
            }
        };
        this.gclid = this.readGclid();
        this.ucc = this.readUcc();
    }

    private readGclid(): string | null {
        const raw = Utils.getCookie('_gcl_aw');
        let result: string | null = null;
        if (raw) {
            const parts = raw.split('.');
            result = parts.length > 2 ? parts[2] : raw;
        }
        return result && result.length <= 99 ? result : null;
    }

    private readUcc(): string | null {
        const raw = Utils.getCookie('cello-referral');
        return raw && raw.length <= 99 ? raw : null;
    }

    resolved(token?: string) {
        const formData: SignUpFormData = {
            firstName: this.firstNameCtrl.value,
            lastName: this.lastNameCtrl.value,
            email: this.emailCtrl.value,
            recaptchaResponse: token,
            billingPlan: this.billingPlan,
            language: this.translate.getBrowserLang(),
            gclid: this.gclid,
            ucc: this.ucc
        };
        this.authService.signUp(toSignUpApiPayload(formData)).subscribe({
            next: () => {
                this.submitInFlight = false;
                this.currentStep = SignUpStep.Confirmation;
                this.gaService.trackG4Event('sign_up_GA4');
            },
            error: (error: HttpErrorResponse) => {
                this.submitInFlight = false;
                this.evaluateErrors(error);
                this.logger.error(error);
            }
        });
    }

    get nameStepReady(): boolean {
        return this.firstNameCtrl.valid && this.lastNameCtrl.valid && this.agreeToTermsCtrl.valid;
    }

    submitEmailStep() {
        if (this.submitInFlight) {
            return;
        }
        this.submitPressed = true;
        if (this.emailCtrl.invalid) {
            return;
        }
        this.submitInFlight = true;
        this.withRecaptcha((token) => this.checkEmail(token));
    }

    private checkEmail(token?: string) {
        this.authService.checkTenantEmail(this.emailCtrl.value.toLowerCase(), token).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.submitInFlight = false;
                this.submitPressed = false;
                this.currentStep = SignUpStep.Name;
            },
            error: (error: HttpErrorResponse) => {
                this.submitInFlight = false;
                this.evaluateErrors(error);
                this.logger.error(error);
            }
        });
    }

    goBack() {
        this.submitPressed = false;
        this.currentStep = SignUpStep.Email;
    }

    signUp() {
        if (this.submitInFlight) {
            return;
        }
        this.submitPressed = true;
        if (!this.form.valid) {
            return;
        }
        this.submitInFlight = true;
        this.withRecaptcha((token) => this.resolved(token));
    }

    private withRecaptcha(next: (token?: string) => void) {
        if (this.settingsService.hideRecaptcha) {
            next();
            return;
        }
        this.recaptchaV3Service.execute('importantAction').pipe(takeUntil(this.destroy$)).subscribe({
            next: (token: string) => next(token),
            error: (error) => {
                this.submitInFlight = false;
                this.logger.error(error);
            }
        });
    }

    @HostListener('document:keydown.enter')
    onKeyEnter() {
        this.stepEnterHandlers[this.currentStep]?.();
    }

    clearErrors() {
        this.errors.length = 0;
    }

    private markEmailServerRejected() {
        this.serverRejectedEmail = this.emailCtrl.value;
        this.emailCtrl.updateValueAndValidity({emitEvent: false});
        this.currentStep = SignUpStep.Email;
    }

    evaluateErrors(serverError: HttpErrorResponse) {
        if (!this.submitPressed) {
            return;
        }
        this.clearErrors();
        if (!serverError && this.form.valid) {
            return;
        }
        this.validationFailed = true;
        const error: SignUpServerErrorBody | undefined = serverError?.error;
        const emailErrorKey = error?.code === 'domain_not_allowed' ? 'EmailError'
            : error?.code === 'email_unusable' ? 'TenantExist'
                : null;
        if (emailErrorKey) {
            this.errors.push(this.translate.get(`${this.transPrefix}${this.formPrefix}${emailErrorKey}`));
            this.markEmailServerRejected();
        } else if (error?.billing_plan) {
            this.errors.push(this.translate.get(`${this.transPrefix}${this.formPrefix}IncorrectLink`));
        } else {
            this.errors.push(this.translate.get(`SignUp.Form.ErrorDefault`));
        }
    }

    ngOnDestroy() {
        this.renderer.removeClass(document.body, 'show-recaptcha');
        this.destroy$.next();
        this.destroy$.complete();
    }

}
